import { useRef, useState, type FormEvent, type RefObject } from "react"
import { toast } from "react-toastify"

import { useSetMetaBrandCredentialMutation } from "~/ui-rtk/api/metaBrandCredentialApi"
// Direct imports (not the `common` / `controls` barrels).
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Button from "~/ui-rtk/components/ui/controls/Button"
import Input from "~/ui-rtk/components/ui/controls/Input"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"

import {
  META_ADVANCED_SETUP_STEPS,
  META_ADVANCED_TOKEN_LABEL,
  META_ADVANCED_TOKEN_NOTE,
  META_ADVANCED_TOKEN_PLACEHOLDER,
  MIN_META_ADVANCED_TOKEN_LENGTH,
} from "./copy"

const DIALOG_CONTAINER_CLS =
  "flex max-h-[90vh] w-full max-w-[95vw] flex-col gap-3 overflow-y-auto rounded-lg border border-basic-blue bg-basic-dark-blue p-5 text-left text-sm text-gray-300 shadow-xl"

export interface MetaAdvancedTokenDialogProps {
  /** The company the page shows; the API refuses the set for any other. */
  companyId: string
  /** A credential is stored: the new token replaces it. */
  replacing: boolean
  onClose: () => void
}

/**
 * Super-admin dialog that sets or replaces the brand's Meta Advanced token
 * (APP-548), after AgencyCredentialDialog's paste flow.
 *
 * The field is uncontrolled on purpose: React mirrors a controlled input's
 * value into its `value` attribute, which would put the token in the DOM.
 * Only its length lives in state, for the 32-character gate. The token is
 * sent once and never rendered; the dialog is mounted only while open, so
 * nothing survives a close. After a refusal the field keeps the token, so a
 * fix on Meta's side (an asset assigned to the system user) can be retried.
 */
export default function MetaAdvancedTokenDialog({
  companyId,
  replacing,
  onClose,
}: MetaAdvancedTokenDialogProps) {
  const [setCredential, { isLoading: saving }] =
    useSetMetaBrandCredentialMutation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [tokenLength, setTokenLength] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const canSubmit = tokenLength >= MIN_META_ADVANCED_TOKEN_LENGTH && !saving

  const close = () => {
    if (inputRef.current) inputRef.current.value = ""
    onClose()
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const input = inputRef.current
    if (!input || !canSubmit) return
    setError(null)
    try {
      await setCredential({
        companyId,
        systemUserAccessToken: input.value.trim(),
      }).unwrap()
      toast.success("Meta Advanced credential saved.")
      close()
    } catch (err) {
      // The API's message is the user-facing verdict (an invalid, expiring or
      // wrong-app token, missing permissions, a system user another company
      // uses, an asset it cannot reach). It never contains the token.
      setError(
        getServerErrorMessage(
          err,
          "Couldn't save the Meta Advanced token. Try again.",
        ),
      )
    }
  }

  return (
    <Dialog
      isOpen
      handleClose={close}
      closeOnClickOutside
      width={560}
      classes={{ container: DIALOG_CONTAINER_CLS }}
    >
      <div>
        <h2 className="text-lg font-semibold text-white">
          {replacing
            ? "Replace the Meta Advanced credential"
            : "Set the Meta Advanced credential"}
        </h2>
        <p className="mt-1 text-gray-400">{META_ADVANCED_TOKEN_NOTE}</p>
      </div>

      <ol className="ml-5 list-decimal leading-5">
        {META_ADVANCED_SETUP_STEPS.map(step => (
          <li key={step}>{step}</li>
        ))}
      </ol>

      <form
        onSubmit={event => void submit(event)}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-1">
          <span>{META_ADVANCED_TOKEN_LABEL}</span>
          <Input
            // `itemRef` clashes with the HTML microdata attribute of the same
            // name; cast as AdForgeDefaultsSection does.
            itemRef={inputRef as string & RefObject<HTMLInputElement>}
            type="password"
            fullWidth
            autoComplete="off"
            spellCheck={false}
            aria-label={META_ADVANCED_TOKEN_LABEL}
            placeholder={META_ADVANCED_TOKEN_PLACEHOLDER}
            onChange={({ target }) =>
              setTokenLength(target.value.trim().length)
            }
            disabled={saving}
          />
        </div>

        {error && (
          <p role="alert" className="text-states-error">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant={{ variant: "text" }}
            onClick={close}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            variant={{ variant: "solid", color: "white" }}
            disabled={!canSubmit}
            isLoading={saving}
          >
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
